import { useState, useCallback } from "react";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import { MobileHeader } from "./MobileHeader";
import { MobileBottomNav } from "./MobileBottomNav";
import { Sidebar } from "./Sidebar";
import { TimerPill } from "./TimerPill";
import type { NavItem } from "./MobileBottomNav";

interface AppShellProps {
	children: React.ReactNode;
	navItems: NavItem[];
	userInitials: string;
	userDisplayName: string;
	userEmail?: string;
	showRoleSwitcher?: boolean;
	currentRole?: "attorney" | "admin";
	onRoleSwitch?: () => void;
}

export function AppShell({
	children,
	navItems,
	userInitials,
	userDisplayName,
	userEmail,
	showRoleSwitcher,
	currentRole,
	onRoleSwitch,
}: AppShellProps) {
	const isMobile = useIsMobile();
	const [collapsed, setCollapsed] = useState(() => {
		try { return localStorage.getItem("v2:sidebar-collapsed") === "true"; } catch { return false; }
	});
	const toggleCollapsed = useCallback(() => {
		setCollapsed((prev) => {
			const next = !prev;
			try { localStorage.setItem("v2:sidebar-collapsed", String(next)); } catch {}
			return next;
		});
	}, []);

	if (isMobile) {
		return (
			<div className="flex h-dvh flex-col bg-background">
				<MobileHeader
					userInitials={userInitials}
					userDisplayName={userDisplayName}
					userEmail={userEmail}
					showRoleSwitcher={showRoleSwitcher}
					currentRole={currentRole}
					onRoleSwitch={onRoleSwitch}
				/>
				<main className="flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
					{children}
				</main>
				<MobileBottomNav items={navItems} />
				<TimerPill />
			</div>
		);
	}

	return (
		<div className="flex h-dvh bg-background">
			<Sidebar
				navItems={navItems}
				userInitials={userInitials}
				userDisplayName={userDisplayName}
				userEmail={userEmail}
				showRoleSwitcher={showRoleSwitcher}
				currentRole={currentRole}
				onRoleSwitch={onRoleSwitch}
				collapsed={collapsed}
				onToggleCollapse={toggleCollapsed}
			/>
			<main className="flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
				{children}
			</main>
			<TimerPill />
		</div>
	);
}
